import { blankEdges } from "./loop";
import {
  CROSSED,
  LINE,
  MAX_SIZE,
  MIN_SIZE,
  NO_PEARL,
  NONE,
  type Edges,
  type MasyuDefinition,
  type MasyuProgress,
  type MasyuSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Draw a single closed loop through the centres of the cells, never crossing or touching itself, passing through every pearl. On a white pearl the loop goes straight, and turns in the cell before or after it. On a black pearl the loop turns, and goes straight through the next cell on both sides.",
  fr: "Tracez une seule boucle fermée passant par le centre des cases, sans qu'elle se croise ni se touche, et qui traverse chaque perle. Sur une perle blanche, la boucle va tout droit et tourne dans la case précédente ou suivante. Sur une perle noire, la boucle tourne et va tout droit dans la case suivante, de chaque côté.",
};

export function emptyDefinition(): MasyuDefinition {
  return {
    puzzleType: "masyu",
    typeVersion: 1,
    rows: 6,
    cols: 6,
    pearls: Array.from({ length: 6 }, () => NO_PEARL.repeat(6)),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return MIN_SIZE;
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.trunc(value)));
}

function fitLines(source: string[], count: number, length: number, fill: string): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => {
    const line = source[i] ?? "";
    const size = Math.max(0, length);
    return line.length >= size ? line.slice(0, size) : line + fill.repeat(size - line.length);
  });
}

/** Crops or pads a pearl grid to the given size, keeping the top-left corner. */
export function resizePearls(pearls: string[], rows: number, cols: number): string[] {
  return fitLines(pearls, rows, cols, NO_PEARL);
}

/** Crops or pads a set of edges to the given grid size, keeping the top-left corner. */
export function resizeEdges(edges: Edges, rows: number, cols: number): Edges {
  return { h: fitLines(edges.h, rows, cols - 1, NONE), v: fitLines(edges.v, rows - 1, cols, NONE) };
}

function sanitize(raw: unknown, allowed: readonly string[]): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((line) =>
    typeof line === "string"
      ? Array.from(line, (char) => (allowed.includes(char) ? char : NONE)).join("")
      : "",
  );
}

/** Reads any `{h, v}` blob as edges of the grid's size, keeping only the allowed marks. */
export function edgesOf(
  raw: unknown,
  rows: number,
  cols: number,
  allowed: readonly string[] = [LINE, NONE],
): Edges {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return blankEdges(rows, cols);
  const { h, v } = raw as { h?: unknown; v?: unknown };
  return resizeEdges({ h: sanitize(h, allowed), v: sanitize(v, allowed) }, rows, cols);
}

export function emptyProgress(definition: MasyuDefinition): MasyuProgress {
  return { version: 1, ...blankEdges(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: MasyuDefinition, raw: unknown): MasyuProgress {
  return {
    version: 1,
    ...edgesOf(raw, definition.rows, definition.cols, [LINE, CROSSED, NONE]),
  };
}

/** The server only reads segments: crosses are the player's own notes. */
export function serializeSubmission(
  definition: MasyuDefinition,
  progress: MasyuProgress,
): MasyuSubmission {
  const edges = resizeEdges(progress, definition.rows, definition.cols);
  return {
    h: edges.h.map((line) => line.replaceAll(CROSSED, NONE)),
    v: edges.v.map((line) => line.replaceAll(CROSSED, NONE)),
  };
}

export function sameEdges(a: Edges, b: Edges): boolean {
  return a.h.join("\n") === b.h.join("\n") && a.v.join("\n") === b.v.join("\n");
}

export function edgesSignature(edges: Edges): string {
  return `${edges.h.join("\n")}|${edges.v.join("\n")}`;
}
